import { useIntl } from 'react-intl';

import { useToaster } from '@/components/base/toaster';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { POOL } from '@/types/channels';

import { useUpdateOpeningDescription } from '../job-details/use-update-opening-description';
import { usePoolForm } from '../pool-setup/use-pool-form';

import type { SourcingOpeningUpdateParameters } from 'api-clients/monolith/models/SourcingOpeningUpdateParameters';

export function useValidatedStepper() {
  const intl = useIntl();
  const { notifyWarning } = useToaster();
  const {
    currentStep,
    setCurrentStep,
    hookForm,
    selectedChannelSlugs,
    selectedOpeningIds,
  } = useJobPostingContext();

  const { updateOpeningDescriptionAsync, isUpdatingOpening } =
    useUpdateOpeningDescription();
  const { validateOverlappingPoolPurchasesAsync, isValidatingPoolPurchases } =
    usePoolForm();

  const {
    trigger,
    formState: { errors },
    getValues,
  } = hookForm;

  const validateStep = async (step: number) => {
    /** Validate job details step */
    if (step === 1) {
      const isValid = await trigger('jobDetails');

      if (!isValid) {
        const errorCount = Object.keys(errors?.jobDetails ?? {}).length ?? 0;

        notifyWarning({
          message: intl.formatMessage(
            {
              defaultMessage:
                '{count, plural, =1 {You have one incomplete job. Please fill it in to continue.} other {You have {count} incomplete jobs. Please fill them in to continue.}}',
              description: 'Error message for incomplete job descriptions',
            },
            { count: errorCount },
          ),
        });
        return false;
      }

      // If there's only one opening, update its job description before moving to the next step
      if (selectedOpeningIds.length === 1) {
        const openingExternalId = selectedOpeningIds[0];

        const requestBody: SourcingOpeningUpdateParameters = {
          description: getValues(`jobDetails.${openingExternalId}.description`),
          state_code: getValues(`jobDetails.${openingExternalId}.stateCode`),
        };

        try {
          await updateOpeningDescriptionAsync({
            openingExternalId,
            requestBody,
          });
        } catch {
          // Error is already handled by the mutation's onError callback
          return false;
        }
      }
    }

    /** Validate pool step */
    if (step === 2) {
      const isValid = await trigger('pool');

      if (!isValid) {
        notifyWarning({
          message: intl.formatMessage({
            defaultMessage: 'Please complete the pool setup to proceed',
            description: 'Error message for incomplete pool setup',
          }),
        });
        return false;
      }

      return validateOverlappingPoolPurchasesAsync();
    }

    /** Validate channel forms step */
    if (step === 3) {
      const isValid = await trigger('channels');

      if (!isValid) {
        /** Validate channel form errors */
        notifyWarning({
          message: intl.formatMessage({
            defaultMessage:
              'Please complete the spend details to proceed to payment.',
            description: 'Error message for incomplete spend details',
          }),
        });
        return false;
      }

      /** Validate if channel forms are saved and validated */
      const channels = getValues('channels');
      const isAllChannelsValidated = Object.values(channels).every(
        channel => channel.isValidated,
      );

      if (!isAllChannelsValidated) {
        notifyWarning({
          message: intl.formatMessage(
            {
              defaultMessage:
                '{count, plural, =1 {Please save the channel form before proceeding.} other {Please save all channel forms before proceeding.}}',
              description: 'Error message for incomplete channel forms',
            },
            { count: selectedChannelSlugs.length },
          ),
        });
        return false;
      }
    }

    return true;
  };

  /* Handle next step button click */
  const handleNext = async () => {
    let isValid = true;

    if (currentStep === 1) {
      isValid = await validateStep(1);
      if (!isValid) return;
    } else if (currentStep === 2) {
      isValid = await validateStep(2);
      if (!isValid) return;
    } else if (currentStep === 3) {
      isValid = await validateStep(3);
      if (!isValid) return;
    }

    let nextStep = currentStep + 1;
    if (currentStep === 1 && !selectedChannelSlugs.includes(POOL)) {
      nextStep = 3; // Skip pool step if pool is not selected and go directly to spend step
    }

    setCurrentStep(nextStep);
  };

  /* Handle when users navigate to a step directly from the stepper */
  const handleStepClick = async (toStep: number) => {
    if (toStep <= currentStep) {
      setCurrentStep(toStep);
      return;
    }

    // Sequential validation is intentional - we need to validate steps in order
    // and stop at the first invalid step to show a single error message
    /* eslint-disable no-await-in-loop */
    for (let step = currentStep; step < toStep; step++) {
      const isValid = await validateStep(step);
      if (!isValid) {
        setCurrentStep(step);
        return;
      }
    }
    /* eslint-enable no-await-in-loop */

    setCurrentStep(toStep);
  };

  return {
    handleNext,
    handleStepClick,
    isLoading: isUpdatingOpening || isValidatingPoolPurchases,
  };
}
